import React, { useState } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import StarRating from './StarRating'
import Modal from './Modal'

interface BookReviewFormProps {
  bookPickId: number
  existingReview?: {
    id: number
    rating: number
    review_text: string | null
  }
  onClose: () => void
  onSuccess: () => void
}

export default function BookReviewForm({ bookPickId, existingReview, onClose, onSuccess }: BookReviewFormProps) {
  const [rating, setRating] = useState(existingReview?.rating || 0)
  const [reviewText, setReviewText] = useState(existingReview?.review_text || '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (rating === 0) {
      setError('Please select a rating')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      const url = existingReview
        ? `/api/v1/book_picks/${bookPickId}/reviews/${existingReview.id}`
        : `/api/v1/book_picks/${bookPickId}/reviews`

      const method = existingReview ? 'PATCH' : 'POST'

      const response = await fetchWithCsrf(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          book_review: {
            rating: rating,
            review_text: reviewText || null
          }
        })
      })

      if (response.ok) {
        onSuccess()
        onClose()
      } else {
        const data = await response.json()
        setError(data.errors?.join(', ') || 'Failed to save review')
      }
    } catch (err) {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const ratingLabels: { [key: number]: string } = {
    1.0: 'Did not like it',
    1.5: 'Really didn\'t like it',
    2.0: 'It was okay',
    2.5: 'Mostly okay',
    3.0: 'Liked it',
    3.5: 'Liked it quite a bit',
    4.0: 'Really liked it',
    4.5: 'Loved it',
    5.0: 'It was amazing'
  }

  return (
    <Modal isOpen={true} onClose={onClose} size="lg" showCloseButton={true}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {existingReview ? 'Edit Your Review' : 'Write a Review'}
          </h2>
          <p className="text-gray-600">Share your thoughts with the group</p>
        </div>
      </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Star Rating */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Your Rating *
            </label>
            <div className="flex items-center gap-4">
              <StarRating
                rating={rating}
                interactive={true}
                size="lg"
                onChange={setRating}
              />
              {rating > 0 && (
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">{rating.toFixed(1)}</span>
                  {' - '}
                  <span>{ratingLabels[rating] || 'Custom rating'}</span>
                </div>
              )}
            </div>
          </div>

          {/* Review Text */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Review (optional)
            </label>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              rows={6}
              placeholder="What did you think? Share your thoughts about the book..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all border-2 border-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || rating === 0}
              className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
            >
              {submitting ? 'Saving...' : existingReview ? 'Update Review' : 'Submit Review'}
            </button>
          </div>
        </form>
    </Modal>
  )
}
